/**
 * Reasoning helpers for the Intelligence thinking pipeline.
 * Centralizes configuration, normalization, and utility functions.
 */

import { Brain, Database, MessageSquare, PenTool, Shield } from "lucide-react"
import type { ReasoningData, ThinkingStep, ThinkingStepConfig } from "@/lib/types/reasoning"

/**
 * Configuration for the 5-step thinking pipeline.
 * Each step maps to a phase of AI reasoning and optionally to a reasoning panel section.
 */
export const THINKING_STEPS_CONFIG: ThinkingStepConfig[] = [
  {
    id: "understand",
    label: "Understand your question",
    icon: MessageSquare,
    microActions: ["Reading your message", "Identifying what you need"],
    reasoningSection: null,
  },
  {
    id: "lookup",
    label: "Look up your business context",
    icon: Database,
    microActions: ["Finding relevant memories", "Checking related dashboards"],
    reasoningSection: "memoriesUsed",
  },
  {
    id: "analyze",
    label: "Analyze your memories",
    icon: Brain,
    microActions: ["Comparing to your goals", "Weighing trade-offs"],
    reasoningSection: "keyFactors",
  },
  {
    id: "verify",
    label: "Check for truth & safety",
    icon: Shield,
    microActions: ["Checking for gaps", "Applying guardrails"],
    reasoningSection: "safetyCheck",
  },
  {
    id: "write",
    label: "Write your answer",
    icon: PenTool,
    microActions: ["Structuring recommendations", "Adding examples"],
    reasoningSection: null,
  },
]

/**
 * Gets the step configuration for a given thinking step.
 * Supports both semantic IDs ("understand") and numeric IDs ("1").
 */
export function getStepConfig(step: ThinkingStep): ThinkingStepConfig | undefined {
  // Try exact match first
  let config = THINKING_STEPS_CONFIG.find((c) => c.id === step.id)
  // Fall back to numeric index if step uses "1", "2", etc.
  if (!config && /^\d+$/.test(step.id)) {
    const index = Number.parseInt(step.id, 10) - 1
    config = THINKING_STEPS_CONFIG[index]
  }
  return config
}

/**
 * Returns the icon component for a thinking step.
 * Falls back to Brain icon if step config not found.
 */
export function getStepIcon(step: ThinkingStep) {
  const config = getStepConfig(step)
  return config?.icon || Brain
}

/**
 * Checks if reasoning data has any displayable content.
 * Returns false for null, undefined, or empty reasoning.
 */
export function hasReasoningContent(reasoning?: ReasoningData): boolean {
  if (!reasoning) return false
  return (
    (reasoning.memoriesUsed && reasoning.memoriesUsed.length > 0) ||
    (reasoning.keyFactors && reasoning.keyFactors.length > 0) ||
    (reasoning.assumptions && reasoning.assumptions.length > 0) ||
    !!reasoning.safetyCheck
  )
}

/**
 * Normalizes reasoning data by applying caps to prevent UI bloat.
 * Returns undefined if input is falsy.
 */
export function normalizeReasoning(reasoning?: ReasoningData): ReasoningData | undefined {
  if (!reasoning) return undefined

  return {
    memoriesUsed: reasoning.memoriesUsed?.slice(0, 8),
    keyFactors: reasoning.keyFactors?.slice(0, 5),
    assumptions: reasoning.assumptions?.slice(0, 5),
    safetyCheck: reasoning.safetyCheck,
  }
}

/**
 * Formats duration in milliseconds to a human-readable string.
 * Examples: 50ms -> "0.1s", 500ms -> "0.50s", 1500ms -> "1.5s", 15000ms -> "15s"
 */
export function formatDuration(ms: number): string {
  const seconds = ms / 1000
  if (seconds < 0.1) return "0.1s"
  if (seconds < 1) return `${seconds.toFixed(2)}s`
  if (seconds < 10) return `${seconds.toFixed(1)}s`
  return `${Math.round(seconds)}s`
}

/**
 * Creates the initial thinking steps array for animation.
 */
export function createInitialThinkingSteps(): ThinkingStep[] {
  return THINKING_STEPS_CONFIG.map((config, index) => ({
    id: config.id,
    label: config.label,
    status: index === 0 ? "active" : "pending",
  }))
}
